import store from "./store";
import Actions from "./Redux/actions";
import { setLtiSession, setLtiInfo } from "./Redux/Users/UserActions";
import { EndPoint } from "./utils/apiClient";
import { lmsMockLtiData } from "./LTI_mock_data";
import { generateLtiUserId } from "./utils/ltiUtils";

/**
 * Decode a JWT's payload without verifying its signature.
 * The client never verifies the LTI session token — the API re-verifies it
 * against its own secret on every request that carries it, so the token is
 * treated as an opaque credential here (same pattern as VLP V2).
 * Returns the payload object, or null if the token is missing or malformed.
 * @param {string} token
 */
export function decodeJwtPayload(token) {
  if (!token) {
    return null;
  }
  const parts = token.split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // atob yields a Latin-1 binary string; decode its bytes as UTF-8 so
    // non-ASCII payload fields (names, institutions) survive intact.
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (err) {
    return null;
  }
}

/**
 * Mirror of V2 parseLtiSession's expiry handling: a payload is expired only
 * when it carries a numeric (or numeric-string) exp claim in the past.
 * @param {Object} payload
 * @private
 */
function isJwtPayloadExpired(payload) {
  if (payload.exp === undefined) {
    return false;
  }
  const expValue = typeof payload.exp === "string" ? Number(payload.exp) : payload.exp;
  if (typeof expValue !== "number" || isNaN(expValue)) {
    return false;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds > expValue;
}

/**
 * Pushes LTI user information to the dataLayer for Google Analytics
 * Includes comprehensive LMS metadata for better analytics segmentation
 * @param {Object} ltiData - The LTI data object
 * @param {boolean} verbose - Whether to log detailed information
 * @private
 */
function pushLtiUserToDataLayer(ltiData, verbose = false) {
  if (!ltiData) return;
  
  // Generate the unique LTI user ID
  const ltiUserId = generateLtiUserId(ltiData);
  
  // Only push to dataLayer if we have a valid user ID
  if (ltiUserId) {
    window.dataLayer = window.dataLayer || [];
    
    // Create a streamlined LTI data object for the dataLayer
    const ltiAnalyticsData = {
      event: 'lti_user_identified',
      userId: ltiUserId,
      
      // Core LTI consumer identifiers
      ltiConsumerGuid: ltiData.tool_consumer_instance_guid || '',
      ltiConsumerName: ltiData.tool_consumer_instance_name || '',
      
      // LMS type and version information
      ltiVersion: ltiData.lti_version || '',
      ltiProductFamily: ltiData.tool_consumer_info_product_family_code || '',
      ltiProductVersion: ltiData.tool_consumer_info_version || '',
      ltiLmsType: ltiData.ext_lms || '',
      
      // Message type only
      ltiMessageType: ltiData.lti_message_type || ''
    };
    
    // Push the LTI data to the dataLayer
    window.dataLayer.push(ltiAnalyticsData);
    
    if (verbose) {
      console.log('[LTI] Pushed LTI user data to dataLayer:', ltiUserId);
      console.log('[LTI] LTI analytics data:', ltiAnalyticsData);
    }
  } else {
    if (verbose) {
      console.warn('[LTI] Unable to generate unique LTI user ID with provided data');
    }
  }
};

export default class LTI {
  constructor() {
    const verbose = false;
    let isLti = false;

    // Set `useMockLtiData` to true to use Mock LTI data locally.
    const useMockLtiData = false;
    // Sets which mock LTI data string to use when mocking. See all option in the `lmsMockLtiData` object
    const mockLtiData = lmsMockLtiData.canvas.deepLinking;

    if (verbose) {
      console.log("env: ", process.env);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get("session");
    if (sessionToken) {
      if (verbose) {
        console.log("Found LTI session: ", sessionToken);
      }

      // Decode the token payload only — no signature verification client-side.
      // The API verifies this same token on every request that uses it.
      const payload = decodeJwtPayload(sessionToken);
      if (!payload) {
        console.log("ERROR: could not decode LTI session token");
      } else if (isJwtPayloadExpired(payload)) {
        console.log("ERROR: LTI session token expired");
      } else {
        if (verbose) {
          console.log("JWT payload is\n", payload);
        }

        // lti processing
        const lti_obj = payload;

        if (verbose) {
          console.log("LTI object: ", lti_obj);
        }

        pushLtiUserToDataLayer(lti_obj, verbose);

        store.dispatch(setLtiInfo(lti_obj));
        store.dispatch(setLtiSession(sessionToken));
        store.dispatch(
          Actions.Channels.fetchLtiChannels(lti_obj.oauth_consumer_key)
        );
        isLti = true;
      }
    } else {
      if (verbose) {
        console.log("LTI session not found in session");
      }
    }

    let lti_mock_data = null;
    if (useMockLtiData && mockLtiData) {
      lti_mock_data = mockLtiData;
    }

    if (lti_mock_data && !isLti) {
      // do lti testing override if it hasn't already been setup
      console.log("LTI mock data override string: ", lti_mock_data);
      const lti_obj = JSON.parse(lti_mock_data);
      console.log("LTI object: ", lti_obj);
      
      pushLtiUserToDataLayer(lti_obj, verbose);
      
      store.dispatch(setLtiInfo(lti_obj));
      store.dispatch(
        Actions.Channels.fetchLtiChannels(lti_obj.oauth_consumer_key)
      );
      isLti = true;
    }
  }
}

export const GetEmbedActivityUrl = (
  channelId,
  activityId,
  activityTitle,
  ltiReturnUrl,
  canvasCourseId,
  canvasApiDomain
) => {
  const apiEndpoint = EndPoint();

  const contentUrl = new URL("/ltiaccess", `${apiEndpoint}`);
  contentUrl.searchParams.set("channelId", channelId);
  contentUrl.searchParams.set("activityId", activityId);
  contentUrl.searchParams.set("embed", 1);

  const ltiToolsUrl = new URL(
    `/courses/${canvasCourseId}/external_tools/retrieve`,
    `https://${canvasApiDomain}`
  );
  ltiToolsUrl.searchParams.set("display", "borderless");
  ltiToolsUrl.searchParams.set("url", contentUrl.href);

  const finalRedirectUrl = new URL(ltiReturnUrl);
  finalRedirectUrl.searchParams.set("return_type", "iframe");
  finalRedirectUrl.searchParams.set("frameborder", 0);
  finalRedirectUrl.searchParams.set("allowfullscreen", true);
  finalRedirectUrl.searchParams.set("title", activityTitle);
  finalRedirectUrl.searchParams.set("width", "100%");
  finalRedirectUrl.searchParams.set("height", "640px");
  finalRedirectUrl.searchParams.set("url", ltiToolsUrl.href);

  return finalRedirectUrl;
};

export const GetActivityLinkUrl = (
  channelId,
  activityId,
  activityTitle,
  ltiReturnUrl
) => {
  const apiEndpoint = EndPoint();

  const contentUrl = new URL("/ltiaccess", `${apiEndpoint}`);
  contentUrl.searchParams.set("channelId", channelId);
  contentUrl.searchParams.set("activityId", activityId);

  const finalRedirectUrl = new URL(ltiReturnUrl);
  finalRedirectUrl.searchParams.set("return_type", "lti_launch_url");
  finalRedirectUrl.searchParams.set("display", "borderless");
  finalRedirectUrl.searchParams.set("title", activityTitle);
  finalRedirectUrl.searchParams.set("text", activityTitle);
  finalRedirectUrl.searchParams.set("target", "_blank");
  finalRedirectUrl.searchParams.set("url", contentUrl.href);

  return finalRedirectUrl;
};

export const GetActivityLinkUrlNoAuth = (
  activityId,
  activityTitle,
  ltiReturnUrl
) => {
  const contentUrl = new URL(
    `${window.location.protocol}//${window.location.hostname}/activity/${activityId}`
  );

  const finalRedirectUrl = new URL(ltiReturnUrl);
  finalRedirectUrl.searchParams.set("return_type", "url");
  finalRedirectUrl.searchParams.set("title", activityTitle);
  finalRedirectUrl.searchParams.set("text", activityTitle);
  finalRedirectUrl.searchParams.set("target", "_blank");
  finalRedirectUrl.searchParams.set("url", contentUrl.href);

  return finalRedirectUrl;
};
